import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { FaTimes } from 'react-icons/fa';
import { useOverlayBack } from '../../hooks/useSmartBack';
import './Modal.scss';

const SWIPE_CLOSE_THRESHOLD = 90;

const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'medium',
  showCloseButton = true,
  headerActions = null,
  className = '',
  contentClassName = '',
  // Participate in step-by-step back navigation: a hardware / browser /
  // swipe back press while this modal is open closes the modal (one press
  // = one layer) instead of leaving the page. Opt out only for blocking
  // overlays that must not be dismissible (e.g. trial-expired gate).
  enableBackHandle = true,
}) => {
  const touchStartRef = useRef(null);
  const modalRef = useRef(null);

  // Register with the global back manager while open.
  useOverlayBack(isOpen && enableBackHandle, onClose || (() => {}), `modal:${title || size}`);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Touch UX: swipe down from the top of the sheet closes it (mobile).
  const handleTouchStart = (e) => {
    if (e.touches.length !== 1) {
      touchStartRef.current = null;
      return;
    }
    const modalTop = modalRef.current?.getBoundingClientRect()?.top ?? 0;
    touchStartRef.current = {
      y: e.touches[0].clientY,
      x: e.touches[0].clientX,
      fromTop: e.touches[0].clientY - modalTop,
    };
  };

  const handleTouchEnd = (e) => {
    const start = touchStartRef.current;
    touchStartRef.current = null;
    if (!start || e.changedTouches.length !== 1) return;
    const dy = e.changedTouches[0].clientY - start.y;
    const dx = Math.abs(e.changedTouches[0].clientX - start.x);
    // Mostly-vertical downward swipe starting near the top of the modal.
    if (dy > SWIPE_CLOSE_THRESHOLD && dx < 60 && start.fromTop < 220) {
      onClose?.();
    }
  };

  return ReactDOM.createPortal(
    <div
      className="modal-overlay"
      onClick={onClose}
      role="presentation"
    >
      <div 
        ref={modalRef}
        className={`modal modal--${size} ${className}`.trim()} 
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : 'Dialog'}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div className="modal__drag-handle" aria-hidden="true" />
        <div className="modal__header">
          <h2 className="modal__title">{title}</h2>
          <div className="modal__header-actions">
            {headerActions}
            {showCloseButton && (
              <button 
                className="modal__close" 
                onClick={onClose}
                aria-label="Close modal"
              >
                <FaTimes />
              </button>
            )}
          </div>
        </div>
        <div className={`modal__content ${contentClassName}`.trim()}>
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default Modal;
